import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { WEBHOOK_ENDPOINTS } from '../config/api';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const WebhookDashboard = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const response = await fetch(WEBHOOK_ENDPOINTS.LIST, {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.webhooks);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch webhooks');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (webhookId) => {
    if (window.confirm('Are you sure you want to delete this webhook?')) {
      try {
        const response = await fetch(WEBHOOK_ENDPOINTS.DETAIL(webhookId), {
          method: 'DELETE',
          headers: getAuthHeader(),
        });

        if (response.ok) {
          setWebhooks(webhooks.filter(webhook => webhook.id !== webhookId));
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to delete webhook');
        }
      } catch (err) {
        setError('Something went wrong. Please try again.');
      }
    }
  };

  const handleEdit = (webhookId) => {
    navigate(`/webhooks/edit/${webhookId}`);
  };

  const handleAddNew = () => {
    navigate('/webhooks/add');
  };

  // Mobile card view for webhooks
  const renderMobileWebhookCards = () => {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        {webhooks.map((webhook) => (
          <Grid item xs={12} key={webhook.id}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom>
                  {webhook.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{wordBreak: "break-word"}}>
                  {webhook.url}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{wordBreak: "break-word"}}>
                  {webhook.type || "Discord"}
                </Typography>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'flex-end' }}>
                <IconButton
                  size="small"
                  onClick={() => handleEdit(webhook.id)}
                  color="primary"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(webhook.id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Desktop table view for webhooks
  const renderDesktopWebhookTable = () => {
    return (
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="webhooks table">
          <TableHead>
            <TableRow>
              <TableCell>Webhook Name</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {webhooks.map((webhook) => (
              <TableRow
                key={webhook.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row" sx={{minWidth: 160}}>
                  {webhook.name}
                </TableCell>
                <TableCell>{webhook.url}</TableCell>
                <TableCell sx={{minWidth: 160}}>{webhook.type || "Discord"}</TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(webhook.id)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(webhook.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 0, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'center' : 'center', 
        mb: 3, 
        mt: 1,
        width: '100%'
      }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: isMobile ? 2 : 0, textAlign: isMobile ? 'center' : 'left' }}>
          My Webhooks
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2,
          width: isMobile ? '100%' : 'auto',
          alignItems: 'center'
        }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            fullWidth={isMobile}
          >
            Add New Webhook
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, width: '100%' }}>
          <CircularProgress />
        </Box>
      ) : webhooks.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="h6" color="text.secondary">
            You don't have any webhooks configured yet.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ mt: 2 }}
          >
            Add Your First Webhook
          </Button>
        </Paper>
      ) : (
        <Box sx={{ width: '100%', flex: 1 }}>
          {isMobile ? renderMobileWebhookCards() : renderDesktopWebhookTable()}
        </Box>
      )}
    </Container>
  );
};

export default WebhookDashboard; 